import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import Image from 'next/image';
import {
  UserIcon,
  ShoppingBagIcon,
  Bars3Icon,
  MagnifyingGlassIcon,
} from '@heroicons/react/20/solid';
import { useAppDispatch, useAppSelector } from 'store';
import DropdownWithArrow from '../DropdownWithArrow';
import { LoginSignupModal, LoginSignupModalType } from '../Modal';
import { H5 } from '../Text';
import { Button } from '../Button';
import Container from '../Container';
import SettingsIcon from 'assets/svgs/SettingsIcon';
import NotificationIcon from 'assets/svgs/NotificationIcon';
import TicketIcon from 'assets/svgs/TicketIcon';
import englishFlag from 'assets/svgs/united-kingdom.svg';
import { exitIcon } from 'assets/svgs/brandIcons';
import TipptourLogo from '/assets/svgs/TipptourLogo';
import { Currencies, CurrencyType } from 'interfaces/payment';
import { actions } from 'reducers/payment';
import { actions as airfareSearchActions } from 'reducers/airfare-search';
import { actions as baseActions } from 'reducers/base';
import { Menu } from '@headlessui/react';
import { useTranslation } from 'next-i18next/pages';
import AllmylesLogo from 'assets/svgs/AllmylesLogo';
import localeConfig from 'assets/localization/config.json';
import rewrites, { findKeyByRewriteUrl } from 'urlTranslations';
import { PACKAGE_TOURS_IN_HOUSE } from 'utils/packageToursSwitch';
import FavouritesLink from 'components/PackageTours/FavouritesLink';
import { translateProductPath } from 'utils/productPaths';
import { parseSearch, toCriteria } from 'utils/packageSearch';
import { languageSwitchTarget } from 'utils/routePath';
import { IS_ALLMYLES, brandConfig } from 'utils/brand';
import { ResumeSearch } from 'interfaces/flights';
import { useAuthContext } from 'context/AuthContext';

const mobileMenuItems = [
  {
    id: 4,
    label: 'Settings',
    to: '/settings/',
    icon: <SettingsIcon className="h-6 w-6" />,
  },
  {
    id: 5,
    label: 'My bookings',
    to: '/bookings/',
    icon: <TicketIcon className="h-6 w-6" />,
  },
  {
    id: 6,
    label: 'Notifications',
    to: '/notifications/',
    icon: <NotificationIcon className="h-6 w-6" />,
  },
  {
    id: 8,
    label: 'Signout',
    to: '/auth/signout',
    icon: <Image src={exitIcon} className="h-6 w-6" alt="" />,
  },
];

interface Props {
  isHome?: boolean;
  navBarClassName?: string;
  verticalDotsClassName?: string;
  isResultsPage?: boolean;
}

const Navbar: React.FC<Props> = ({
  isHome,
  navBarClassName,
  isResultsPage,
}) => {
  const router = useRouter();
  const [searchNextAction, setSearchNextAction] = useState<ResumeSearch | null>(
    null,
  );
  const { authUser: user } = useAuthContext();
  const { t } = useTranslation();

  const paymentCurrency = useAppSelector(
    ({ payment }) => payment.paymentCurrency,
  );
  const showSearchField = useAppSelector(
    ({ airfareSearch }) => airfareSearch.showSearchField,
  );

  const navBarRef = useRef<any>(null);

  const handleNavBarStickyness = () => {
    const sticky = navBarRef.current.offsetTop;
    if (sticky) {
      if (window.scrollY >= sticky + 1) {
        setStickyNavbar(true);
      } else {
        setStickyNavbar(false);
      }
    }
  };

  const [stickyNavbar, setStickyNavbar] = useState(false);
  const [isLoginSignupModalOpen, setLoginSignupModalOpen] = useState(false);
  const [loginSignupModalType, setLoginSignupModalType] =
    useState<LoginSignupModalType>(LoginSignupModalType.LOGIN);
  const dispatch = useAppDispatch();

  const setPaymentCurrency = (curr: CurrencyType) =>
    dispatch(actions.setPaymentCurrency(curr));

  const setShowSearchField = () =>
    dispatch(airfareSearchActions.setShowSearchField(!showSearchField));

  const openLoginModal = () => {
    setLoginSignupModalType(LoginSignupModalType.LOGIN);
    setLoginSignupModalOpen(true);
  };

  const openSignupModal = () => {
    setLoginSignupModalType(LoginSignupModalType.SIGNUP);
    setLoginSignupModalOpen(true);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleNavBarStickyness);

    return () => {
      window.removeEventListener('scroll', handleNavBarStickyness);
    };
  }, []);

  const onToggleLanguageClick = (newLocale: string) => {
    if (newLocale !== router.locale) {
      const { pathname, query } = router;
      // WHIT-482/505: a package-tour page's address holds the product (and
      // the hotel page's the hotel), so it is translated from the address
      // bar rather than looked up.
      const productPage = translateProductPath(
        router.asPath.split('?')[0],
        newLocale,
      );
      if (productPage) {
        const search = router.asPath.includes('?')
          ? router.asPath.slice(router.asPath.indexOf('?'))
          : '';
        router.push(`${productPage}${search}`, undefined, {
          locale: newLocale,
        });
        return;
      }
      // Redirect to the translated url. WHIT-485: a route with a variable
      // part (/holidays/[slug]) is looked up with the part filled in.
      router.push(
        languageSwitchTarget(pathname, query, newLocale, rewrites),
        undefined,
        { locale: newLocale },
      );
    }
  };

  const selectedLanguage = useMemo(() => {
    return localeConfig?.locales?.find(l => l?.locale === router.locale);
  }, [router.locale]);

  const renderDropDowns = (hide?: boolean) => (
    <>
      <DropdownWithArrow
        label={paymentCurrency}
        containerClassName={
          hide
            ? `max-lg:hidden hover:rounded hover:bg-tt-light-gray`
            : 'lg:hover:bg-tt-light-gray lg:hover:rounded'
        }
        menuItemsClassName="text-sm text-gray-700 w-32 origin-top-right right-0"
        pointerCursor
      >
        <ul>
          {(Object.keys(Currencies) as CurrencyType[]).map(curr => (
            <Menu.Item
              as="button"
              className="w-full"
              key={curr}
              onClick={() => setPaymentCurrency(curr)}
            >
              <div className="flex flex-1 justify-between p-3 hover:bg-tt-verylight-purple hover:rounded-xl">
                <H5>{curr}</H5>
                <span className="h-5 w-5">
                  <H5 bolded>{Currencies[curr]}</H5>
                </span>
              </div>
            </Menu.Item>
          ))}
        </ul>
      </DropdownWithArrow>
      <DropdownWithArrow
        logo={
          selectedLanguage ? (
            <Image
              src={selectedLanguage.src}
              style={{ color: undefined }}
              className="h-6 w-5"
              width={20}
              height={24}
              alt={selectedLanguage.label}
            />
          ) : (
            <Image
              src={englishFlag}
              style={{ color: undefined }}
              width={20}
              height={24}
              alt="English"
            />
          )
        }
        className="align-middle"
        containerClassName={
          hide
            ? `max-lg:hidden hover:rounded hover:bg-tt-light-gray`
            : 'lg:hover:bg-tt-light-gray lg:hover:rounded'
        }
        menuItemsClassName="text-sm text-gray-700 w-32 origin-top-right right-0"
        pointerCursor
      >
        {localeConfig?.locales && (
          <ul className="max-h-64 overflow-y-auto">
            {localeConfig.locales.map(
              lang =>
                lang &&
                process.env.NEXT_PUBLIC_I18N_SUPPORTED_LANGS?.includes(
                  lang.locale,
                ) && (
                  <Menu.Item
                    key={lang.localeWithRegion}
                    as="button"
                    className="w-full"
                    onClick={() => onToggleLanguageClick(lang.locale || '')}
                  >
                    <div className="flex text-left flex-1 justify-between p-3 items-center hover:bg-tt-verylight-purple hover:rounded-xl">
                      <H5>{lang.label}</H5>
                      <Image
                        width={20}
                        height={24}
                        src={lang.src}
                        style={{ color: undefined }}
                        alt={lang.localeWithRegion}
                      />
                    </div>
                  </Menu.Item>
                ),
            )}
          </ul>
        )}
      </DropdownWithArrow>
    </>
  );

  const handleNavigation = (to: string) => {
    if (to === '/auth/signout') {
      router.push('/auth/signout');
    } else {
      if (!user?.uid || user?.isAnonymous) {
        setSearchNextAction({
          next: findKeyByRewriteUrl(to, router.locale),
          query: '',
        });
        setLoginSignupModalOpen(true);
        return;
      }

      router.push(findKeyByRewriteUrl(to, router.locale));
    }
  };

  const renderProfileMenuContent = (isMobile: boolean) => {
    return (
      <>
        <ul className={isMobile ? 'flex flex-col' : 'p'}>
          {user?.email && (
            <div className="truncate flex-1 p-2 pl-4 lg:hidden">
              {user?.email}
            </div>
          )}
          {mobileMenuItems.map(item => {
            if (item.label === 'Signout' && !user?.email) return null;
            return (
              <button
                type="button"
                key={item.id}
                className="flex items-center flex-1 p-2 pl-4 space-x-4 hover:bg-tt-verylight-purple hover:rounded-xl w-full"
                onClick={() => handleNavigation(item.to)}
              >
                <span className="mr-3">{item.icon}</span>
                {t(`${item.label}`)}
              </button>
            );
          })}
        </ul>
      </>
    );
  };

  const navbarStyle = `${
    stickyNavbar ? 'fixed w-full bg-white opacity-100 shadow-lg' : 'absolute'
  } top-0 transition-all ease-in-out py-6 z-50`;

  return (
    <div className={`relative z-50 ${isResultsPage && 'mt-4'}`}>
      <LoginSignupModal
        closeModal={() => {
          setLoginSignupModalOpen(false);
          setSearchNextAction(null);
        }}
        modalType={loginSignupModalType}
        modalOpen={isLoginSignupModalOpen}
        content={searchNextAction}
      />
      <Container containerClassName={isHome ? navbarStyle : navBarClassName}>
        <nav ref={navBarRef} className={`dm-sans ${isResultsPage && 'mt-4'}`}>
          <div className="flex flex-wrap justify-between relative items-center">
            <div className="flex items-center w-full lg:w-auto justify-between">
              <Link
                href="/"
                locale={router.locale}
                className="pr-12 lg:pr-8 lg:mt-1 2xl:h-8 2xl:w-[12.5rem] "
                aria-label={brandConfig.name}
              >
                {IS_ALLMYLES ? (
                  <AllmylesLogo />
                ) : (
                  <TipptourLogo className="w-[8.75rem] h-8" />
                )}
              </Link>
              <div className="flex space-x-2 justify-center items-center">
                {router.pathname === '/cart' && (
                  <button
                    onClick={() => dispatch(baseActions.setCartModalOpen(true))}
                    // The bag opens the cart summary; say so (WHIT-494).
                    aria-label={t('Cart') || ''}
                    className="flex-1 lg:hidden mt-[3px]"
                  >
                    <ShoppingBagIcon className="h-7 w-7 text-tt-dark-purple" />
                  </button>
                )}
                {/* The hotel list folds its search away on phones as
                    flight results do (WHIT-500) — only once the search
                    has dates; without them the search stays open, and a
                    toggle would report a state it does not control. The
                    same test as the page's. */}
                {(router.pathname === '/results' ||
                  (router.pathname === '/package-tours/hotels' &&
                    !!toCriteria(parseSearch(router.query)))) && (
                  <button
                    type="button"
                    onClick={setShowSearchField}
                    aria-label={t('Search') || ''}
                    aria-expanded={showSearchField}
                    className="sm:hidden mt-auto rounded-full z-50"
                  >
                    <MagnifyingGlassIcon
                      aria-hidden="true"
                      className="h-7 w-7 rounded-full text-tt-dark-purple"
                    />
                  </button>
                )}
                {PACKAGE_TOURS_IN_HOUSE && (
                  <FavouritesLink className="inline-flex lg:hidden" />
                )}
                <DropdownWithArrow
                  label=""
                  ariaLabel={t('Menu') || ''}
                  dropDownArrowIcon={
                    <Bars3Icon className="h-7 w-7 rounded-full z-50 text-tt-dark-purple" />
                  }
                  containerClassName="!pl-0"
                  className="lg:hidden flex-1 -!right-[10px] md:right-0 hover:rounded hover:bg-tt-light-gray"
                  menuItemsClassName="z-20 top-0 rounded-none rounded-l-xl rounded-bl-xl mt-0 top-0 -right-[10px]"
                  mobileMenuStyle="!p-0 my-auto mt-1"
                  pointerCursor
                >
                  <ul className="pb-4 text-base dm-sans">
                    {renderProfileMenuContent(true)}
                    {!user?.email && (
                      <>
                        <li className="p-2 pl-[3.2rem] hover:bg-tt-verylight-purple hover:rounded-xl">
                          <button onClick={openLoginModal}>
                            {t('Sign in')}
                          </button>
                        </li>
                        <li className="p-2 pl-[3.2rem] hover:bg-tt-verylight-purple hover:rounded-xl">
                          <button onClick={openSignupModal}>
                            {t('Sign up')}
                          </button>
                        </li>
                      </>
                    )}
                  </ul>
                  <div className="flex justify-around w-full py-4 pl-8 pr-2 h-auto self-end text-md rounded-b-xl bg-tt-verylight-purple">
                    {renderDropDowns()}
                  </div>
                </DropdownWithArrow>
              </div>
            </div>
            <div className="navBarItemContainer space-x-4 xl:space-x-6">
              {PACKAGE_TOURS_IN_HOUSE && (
                <FavouritesLink className="hidden lg:inline-flex" />
              )}
              {renderDropDowns(true)}
              {!user?.email && (
                <>
                  <Button
                    onClick={openLoginModal}
                    className="hidden lg:inline-block !py-0 !px-3 hover:bg-[#d8d8d8]"
                  >
                    {t('Sign in')}
                  </Button>
                  <Button
                    onClick={openSignupModal}
                    className="hidden lg:inline-block !py-0 !px-3 hover:bg-[#d8d8d8]"
                  >
                    {t('Sign up')}
                  </Button>
                </>
              )}
              <DropdownWithArrow
                label={user?.email ? user?.email : ''}
                ariaLabel={user?.email ? undefined : t('Menu') || ''}
                spanClassName="truncate max-w-[150px] mt-[2px] xl:max-w-[250px] text-right"
                dropDownArrowIcon={
                  user?.email ? (
                    <UserIcon className="h-5 w-5 text-tt-dark-purple" />
                  ) : (
                    <Bars3Icon className="h-6 w-6 text-tt-dark-purple" />
                  )
                }
                containerClassName="max-lg:hidden space-x-1 hover:rounded hover:bg-tt-light-gray"
                menuItemsClassName="origin-top-right right-0"
                pointerCursor
              >
                {renderProfileMenuContent(false)}
              </DropdownWithArrow>
            </div>
          </div>
        </nav>
      </Container>
    </div>
  );
};

export default Navbar;
